import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import { WithId } from '@/types/firestore';

// ==================== TYPES ====================

export type AdminAlertType =
  | 'new_provider'
  | 'new_customer'
  | 'new_listing'
  | 'provider_verification_pending'
  | 'provider_verification_submitted'
  | 'provider_documents_updated'
  | 'new_order'
  | 'review_flagged'
  | 'voice_followup_no_whatsapp'
  | 'voice_followup_dispatch_failed'
  | 'system';

export interface AdminAlert {
  type: AdminAlertType;
  title: string;
  message: string;
  entityId?: string;
  entityType?: 'provider' | 'customer' | 'listing' | 'order' | 'review';
  entityName?: string;
  data?: Record<string, any>;
  read: boolean;
  createdAt: Timestamp;
}

const ADMIN_ALERTS_COLLECTION = 'adminAlerts';

// ==================== ADMIN ALERTS ====================

/**
 * Fetch admin alerts
 */
export async function fetchAdminAlerts(
  limitCount: number = 50,
  unreadOnly: boolean = false
): Promise<WithId<AdminAlert>[]> {
  const alertsRef = collection(db, ADMIN_ALERTS_COLLECTION);

  let q;
  if (unreadOnly) {
    q = query(
      alertsRef,
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
  } else {
    q = query(alertsRef, orderBy('createdAt', 'desc'), limit(limitCount));
  }

  const snapshot = await getDocs(q);

  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  } as WithId<AdminAlert>));
}

/**
 * Subscribe to admin alerts in real-time
 */
export function subscribeToAdminAlerts(
  callback: (alerts: WithId<AdminAlert>[]) => void,
  limitCount: number = 20
): Unsubscribe {
  const alertsRef = collection(db, ADMIN_ALERTS_COLLECTION);
  const q = query(
    alertsRef,
    where('read', '==', false),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  return onSnapshot(q, (snapshot) => {
    const alerts = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    } as WithId<AdminAlert>));
    callback(alerts);
  }, (error) => {
    console.error('❌ Admin alerts subscription error:', error);
    console.error('If this is a missing index error, click the link above to create it.');
  });
}

/**
 * Get unread alerts count
 */
export async function getUnreadAlertsCount(): Promise<number> {
  const alertsRef = collection(db, ADMIN_ALERTS_COLLECTION);
  const q = query(alertsRef, where('read', '==', false));
  const snapshot = await getDocs(q);
  return snapshot.size;
}

/**
 * Mark alert as read
 */
export async function markAlertAsRead(alertId: string): Promise<void> {
  const alertRef = doc(db, ADMIN_ALERTS_COLLECTION, alertId);
  await updateDoc(alertRef, { read: true });
}

/**
 * Mark all alerts as read
 */
export async function markAllAlertsAsRead(): Promise<void> {
  const alertsRef = collection(db, ADMIN_ALERTS_COLLECTION);
  const q = query(alertsRef, where('read', '==', false));
  const snapshot = await getDocs(q);

  const updates = snapshot.docs.map((docSnap) =>
    updateDoc(doc(db, ADMIN_ALERTS_COLLECTION, docSnap.id), { read: true })
  );

  await Promise.all(updates);
}

/**
 * Create an admin alert for new service provider onboarding
 *
 * Usage: Call this function when a new service provider registers.
 * This should be triggered from:
 * 1. The mobile app after successful provider registration, OR
 * 2. A Cloud Function that listens to SERVICE_PROVIDERS collection onCreate:
 *
 *    exports.onNewProvider = functions.firestore
 *      .document('serviceProviders/{providerId}')
 *      .onCreate(async (snap, context) => {
 *        const provider = snap.data();
 *        await admin.firestore().collection('adminAlerts').add({
 *          type: 'new_provider',
 *          title: 'New Service Provider Registered',
 *          message: `${provider.name} from ${provider.location?.city} has registered.`,
 *          entityId: context.params.providerId,
 *          entityType: 'provider',
 *          entityName: provider.name,
 *          read: false,
 *          createdAt: admin.firestore.Timestamp.now(),
 *        });
 *      });
 */
export async function createNewProviderAlert(
  providerId: string,
  providerName: string,
  providerCity?: string,
  providerType?: string
): Promise<string> {
  const alertsRef = collection(db, ADMIN_ALERTS_COLLECTION);

  const alert: AdminAlert = {
    type: 'new_provider',
    title: 'New Service Provider Registered',
    message: `${providerName} from ${providerCity || 'Unknown'} has registered as a ${providerType || 'service provider'}. Review their profile and documents.`,
    entityId: providerId,
    entityType: 'provider',
    entityName: providerName,
    data: {
      city: providerCity,
      providerType,
    },
    read: false,
    createdAt: Timestamp.now(),
  };

  const docRef = await addDoc(alertsRef, alert);
  return docRef.id;
}

/**
 * Create an admin alert when provider submits verification documents
 */
export async function createVerificationSubmittedAlert(
  providerId: string,
  providerName: string,
  documentType: string
): Promise<string> {
  const alertsRef = collection(db, ADMIN_ALERTS_COLLECTION);

  const alert: AdminAlert = {
    type: 'provider_verification_submitted',
    title: 'Documents Submitted for Verification',
    message: `${providerName} has submitted ${documentType} for verification. Please review.`,
    entityId: providerId,
    entityType: 'provider',
    entityName: providerName,
    data: {
      documentType,
    },
    read: false,
    createdAt: Timestamp.now(),
  };

  const docRef = await addDoc(alertsRef, alert);
  return docRef.id;
}

/**
 * Create a generic admin alert
 */
export async function createAdminAlert(
  type: AdminAlertType,
  title: string,
  message: string,
  entityId?: string,
  entityType?: AdminAlert['entityType'],
  entityName?: string,
  data?: Record<string, any>
): Promise<string> {
  const alertsRef = collection(db, ADMIN_ALERTS_COLLECTION);

  const alert: AdminAlert = {
    type,
    title,
    message,
    entityId,
    entityType,
    entityName,
    data,
    read: false,
    createdAt: Timestamp.now(),
  };

  const docRef = await addDoc(alertsRef, alert);
  return docRef.id;
}

/** Resolve an admin-dashboard route from an alert, including nested CF `data` fields. */
export function getAdminAlertRoute(alert: WithId<AdminAlert> | AdminAlert): string {
  const id = alert.entityId || (alert.data?.entityId as string | undefined);
  const entityType = alert.entityType || (alert.data?.entityType as string | undefined);
  switch (entityType) {
    case 'provider':
    case 'serviceProvider':
      return id ? `/admin/providers?open=${id}` : '/admin/providers';
    case 'listing':
      return id ? `/admin/listings?highlight=${id}` : '/admin/listings';
    case 'customer':
      return id ? `/admin/customers?highlight=${id}` : '/admin/customers';
    case 'order':
      return id ? `/admin/orders?highlight=${id}` : '/admin/orders';
    case 'review':
      return '/admin/reviews';
    case 'fbLead':
      return '/admin/leads';
    default:
      return '/admin/notifications-center';
  }
}
